"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  resetToSystemTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => {
    // Check if we're in the browser
    if (typeof window === "undefined") return "light";

    const savedTheme = localStorage.getItem("kartly-theme") as Theme | null;
    if (savedTheme === "light" || savedTheme === "dark") {
      return savedTheme;
    }

    // No saved preference, use system preference
    if (window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return "light";
  });

  const [userHasManuallySet, setUserHasManuallySet] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("kartly-theme-manual") === "true";
  });

  useEffect(() => {
    setMounted(true);

    // Apply initial theme immediately without animation on first mount
    const root = document.documentElement;
    const body = document.body;

    if (theme === "dark") {
      root.classList.add("dark");
      body.classList.add("dark");
    } else {
      root.classList.remove("dark");
      body.classList.remove("dark");
    }
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Apply theme to document with animation
    const root = document.documentElement;
    const body = document.body;

    // Create theme transition effect only after initial mount
    const transitionElement = document.createElement("div");
    transitionElement.className =
      theme === "dark" ? "theme-transition-dark" : "theme-transition-light";
    document.body.appendChild(transitionElement);

    // Remove transition element after animation
    setTimeout(() => {
      if (transitionElement.parentNode) {
        transitionElement.parentNode.removeChild(transitionElement);
      }
    }, 1200);

    if (theme === "dark") {
      root.classList.add("dark");
      body.classList.add("dark");
    } else {
      root.classList.remove("dark");
      body.classList.remove("dark");
    }

    // Save theme preference
    localStorage.setItem("kartly-theme", theme);
  }, [theme, mounted]);

  // Listen for system theme changes only if user hasn't manually set a preference
  useEffect(() => {
    if (typeof window === "undefined" || !mounted) return;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const systemThemeListener = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      if (!userHasManuallySet) {
        const newTheme = e.matches ? "dark" : "light";
        setTheme(newTheme);
        localStorage.setItem("kartly-theme", newTheme);
      }
    };

    mql.addEventListener("change", systemThemeListener);
    return () => {
      mql.removeEventListener("change", systemThemeListener);
    };
  }, [userHasManuallySet, mounted]);

  const toggleTheme = () => {
    setUserHasManuallySet(true);
    localStorage.setItem("kartly-theme-manual", "true");
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const setThemeManually = (newTheme: Theme) => {
    setUserHasManuallySet(true);
    localStorage.setItem("kartly-theme-manual", "true");
    setTheme(newTheme);
  };

  const resetToSystemTheme = () => {
    setUserHasManuallySet(false);
    localStorage.removeItem("kartly-theme-manual");
    localStorage.removeItem("kartly-theme");

    if (typeof window !== "undefined" && window.matchMedia) {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      setTheme(systemTheme);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        setTheme: setThemeManually,
        resetToSystemTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
